import React, { useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/apiClient";

const DemandeSoftwareForm = () => {
  // State declarations
  const [formData, setFormData] = useState({
    descriptionBesoin: "",
    localite: "",
    nomCompagnie: "",
    nomClient: "",
    prenomClient: "",
    adresseEmail: "",
    numeroTel: "",
  });

  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Form field configuration
  const formFields = [
    {
      label: "Nom du contact",
      name: "nomClient",
      type: "text",
    },
    {
      label: "Prénom du contact",
      name: "prenomClient",
      type: "text",
    },
    {
      label: "Description du besoin",
      name: "descriptionBesoin",
      type: "textarea",
      rows: 4,
    },
    {
      label: "Localité",
      name: "localite",
      type: "text",
    },
    {
      label: "Nom de la compagnie",
      name: "nomCompagnie",
      type: "text",
    },
    {
      label: "Email",
      name: "adresseEmail",
      type: "email",
    },
    {
      label: "Numéro de téléphone",
      name: "numeroTel",
      type: "tel",
    },
  ];

  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle file input changes
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setImages(selectedFiles);

    // Clear image error if it exists
    if (errors.images) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.images;
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.descriptionBesoin.trim()) {
      newErrors.descriptionBesoin = "La description du besoin est requise.";
    }

    if (!formData.localite.trim()) {
      newErrors.localite = "La localité est requise.";
    }

    if (!formData.nomCompagnie.trim()) {
      newErrors.nomCompagnie = "Le nom de la compagnie est requis.";
    }

    if (!formData.adresseEmail.trim()) {
      newErrors.adresseEmail = "L'email est requis.";
    } else if (!/\S+@\S+\.\S+/.test(formData.adresseEmail)) {
      newErrors.adresseEmail = "Veuillez entrer un email valide.";
    }

    if (!formData.numeroTel.trim()) {
      newErrors.numeroTel = "Le numéro de téléphone est requis.";
    }

    if (images.length > 4) {
      newErrors.images = "Vous pouvez télécharger un maximum de 4 images.";
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage("");

    try {
      const formDataToSend = new FormData();

      // Append text fields
      formDataToSend.append("description_besoin", formData.descriptionBesoin);
      formDataToSend.append("localite", formData.localite);
      formDataToSend.append("nom_compagnie", formData.nomCompagnie);
      formDataToSend.append("nom_client", formData.nomClient);
      formDataToSend.append("prenom_client", formData.prenomClient);
      formDataToSend.append("adresse_email", formData.adresseEmail);
      formDataToSend.append("numero_tel", formData.numeroTel);

      // Append images
      images.forEach((image, index) => {
        formDataToSend.append("images", image);
      });

      const response = await apiClient.post(
        `${process.env.REACT_APP_API_URL}/demandeslogiciels`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // Reset form on success
      setSuccessMessage("Votre demande a été envoyée avec succès !");
      setFormData({
        descriptionBesoin: "",
        localite: "",
        nomCompagnie: "",
        nomClient: "",
        prenomClient: "",
        adresseEmail: "",
        numeroTel: "",
      });
      setImages([]);
    } catch (error) {
      console.error("Erreur:", error);
      setErrors({
        submit:
          error.response?.data?.message ||
          "Une erreur est survenue. Veuillez réessayer.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="main-page-wrapper p0">
      <div className="user-data-page clearfix d-lg-flex">
        {/* Left side - Illustration */}
        <div className="illustration-wrapper d-flex align-items-center justify-content-between flex-column">
          <div className="illustration-holder">
            <h3 className="font-rubik">
              Nous avons un plan "stratégique", il s'appelle faire les choses.
            </h3>
            <img
              src="images/assets/planning-doing.png"
              alt="Illustration"
              className="illustration"
            />
          </div>
        </div>

        {/* Right side - Form */}
        <div className="form-wrapper">
          {/* Header */}
          <div className="d-flex justify-content-between">
            <div className="logo">
              <Link to="/">
                <img src="images/logo/logo 1.png" alt="Logo" />
              </Link>
            </div>
            <Link to="/" className="font-rubik go-back-button">
              Retour sur l'accueil
            </Link>
          </div>

          <h2>Expression de besoin pour solution logicielle</h2>

          {/* Success Message */}
          {successMessage && (
            <div className="col-12">
              <div className="alert alert-success">{successMessage}</div>
            </div>
          )}

          <form className="user-data-form mt-30" onSubmit={handleSubmit}>
            <div className="row">
              {/* Form Fields */}
              {formFields.map((field) => (
                <div className="col-12" key={field.name}>
                  <div className="input-group-meta mb-50">
                    <label htmlFor={field.name}>{field.label}</label>

                    {field.type === "textarea" ? (
                      <textarea
                        id={field.name}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        className="form-control"
                        rows={field.rows || 4}
                        required={
                          field.name !== "nomClient" &&
                          field.name !== "prenomClient"
                        }
                      />
                    ) : (
                      <input
                        id={field.name}
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        className="form-control"
                        required={
                          field.name !== "nomClient" &&
                          field.name !== "prenomClient"
                        }
                      />
                    )}

                    {errors[field.name] && (
                      <p className="text-danger small mt-1">
                        {errors[field.name]}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* File Upload */}
              <div className="col-12">
                <div className="input-group-meta mb-50">
                  <label htmlFor="images">Images (Maximum 4)</label>
                  <input
                    id="images"
                    type="file"
                    multiple
                    accept="image/*"
                    className="form-control"
                    onChange={handleFileChange}
                  />

                  {errors.images && (
                    <p className="text-danger small mt-1">{errors.images}</p>
                  )}

                  <small className="text-muted">
                    {images.length > 0
                      ? `${images.length} image(s) sélectionnée(s)`
                      : "Vous pouvez sélectionner jusqu'à 4 images"}
                  </small>
                </div>
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="col-12">
                  <div className="alert alert-danger">{errors.submit}</div>
                </div>
              )}

              {/* Submit Button */}
              <div className="col-12">
                <button
                  type="submit"
                  className={`theme-btn-five mt-30 mb-50 ${
                    isLoading ? "disabled-btn" : ""
                  }`}
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Envoi en cours..."
                    : "Transmettre ma demande de logiciel au service concerné"}
                </button>
              </div>

              {/* Footer */}
              <div className="col-12">
                <p className="text-center font-rubik copyright-text">
                  © 2025 logiciel.dvelgegroup.com. Tous droits réservés.
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DemandeSoftwareForm;
